import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import * as s from '../../../../components/TablePaginator/styles';
import { parametersApi } from '../../../../services/requests';
import { Parameter } from '../../../../types/apiResponse/Parameters';

import ContentContainter from '../../../../templates/Content/ContentContainer';

import AsyncTable, { Column } from '../../../../components/AsyncTable';
import getGenericRequestPageForAsyncTable from '../../../../utils/TablesFunctions';
import ChangeParameterValue from './Components/ChangeParameterValue';

const Parameters = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [parameterSelected, setParameterSelected] = useState<Parameter | null>(null);
  const [updateTable, setUpdateTable] = useState(0);

  const handleTableEditBtn = (row: Parameter) => {
    setParameterSelected(row);
    setShowEditModal(true);
  };

  const actionsButtons = (row: Parameter) => (
    <s.ActionButtonsContainer>
      <s.EditIconTable onClick={() => handleTableEditBtn(row)} />
    </s.ActionButtonsContainer>
  );

  const handleValueColumn = (row: Parameter) => {
    if (row.value_type === 'boolean') {
      if (row.boolean_value) {
        return <div>{t('yes')}</div>;
      } else {
        return <div>{t('no')}</div>;
      }
    }

    if (row.value_type === 'array') {
      let arr: string[] = [];
      try {
        if (typeof row.valor === 'string') {
          arr = JSON.parse(row.valor);
        } else if (Array.isArray(row.valor)) {
          arr = row.valor;
        }
      } catch {}
      if (!arr || arr.length === 0) return <div>-</div>;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
          {arr.map((item, idx) => (
            <span
              key={idx}
              style={{
                background: theme.colors.primary,
                color: theme.colors.onPrimary,
                borderRadius: '0.3rem',
                padding: '0.2rem 0.5rem',
                minWidth: 'fit-content',
                fontWeight: 500,
              }}
            >
              {item}
            </span>
          ))}
        </div>
      );
    }

    return <div>{row.valor}</div>;
  };

  const columns: Column<Parameter>[] = [
    {
      label: t('parameter') as string,
      accessor: 'parametro',
    },
    {
      label: t('value') as string,
      accessor: 'valor',
      Cell: handleValueColumn,
    },
    {
      label: t('description') as string,
      accessor: 'descricao',
      cellStyle: {
        maxWidth: '200px',
      },
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  const handleChangeParameterCallback = () => {
    setShowEditModal(false);
    setParameterSelected(null);
    setUpdateTable(updateTable + 1);
  };

  return (
    <ContentContainter title={t('parameters')}>
      <AsyncTable
        tableName={t('parameters')}
        columns={columns}
        value={parameters}
        onChange={setParameters}
        requestPage={getGenericRequestPageForAsyncTable(parametersApi)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />

      {showEditModal ? (
        <ChangeParameterValue
          showModal={showEditModal}
          closeModal={() => setShowEditModal(false)}
          callback={handleChangeParameterCallback}
          parameterSelected={parameterSelected as Parameter}
        />
      ) : (
        <div />
      )}
    </ContentContainter>
  );
};

export default Parameters;
